import { Element, ElementType } from '../../types/drawing';

export type ValidationError = {
  code: string;
  message: string;
  path?: string[];
};

export type Result<T, E> =
  | { ok: true; value: T }
  | { ok: false; error: E };

export function validateElement(input: Element): Result<Element, ValidationError[]> {
  const errs: ValidationError[] = [];

  // Basic identity and type validation
  if (typeof input.id !== 'string' || input.id.trim() === '') {
    errs.push({
      code: 'id.invalid',
      message: 'id must be a non-empty string',
      path: ['id'],
    });
  }
  if (!isValidType(input.type)) {
    errs.push({
      code: 'type.invalid',
      message: `type ${String(input.type)} is not a valid ElementType`,
      path: ['type'],
    });
  }
  if (input.type === 'eraser') {
    errs.push({
      code: 'type.unsupported',
      message: 'eraser is an interaction tool and must not be persisted as an element',
      path: ['type'],
    });
  }

  // Opacity between 0 and 1 inclusive
  if (!Number.isFinite(input.fillOpacity) || input.fillOpacity < 0 || input.fillOpacity > 1) {
    errs.push({
      code: 'opacity.out_of_range',
      message: `fillOpacity ${input.fillOpacity} must be between 0 and 1`,
      path: ['fillOpacity'],
    });
  }

  // Stroke width non-negative
  if (!Number.isFinite(input.strokeWidth) || input.strokeWidth < 0) {
    errs.push({
      code: 'strokeWidth.negative',
      message: `strokeWidth ${input.strokeWidth} must be >= 0`,
      path: ['strokeWidth'],
    });
  }

  // Color strings: accept CSS hex (#RGB, #RGBA, #RRGGBB, #RRGGBBAA), rgb()/rgba(), hsl()/hsla(), named colors, and 'transparent'
  if (!isColorLike(input.strokeColor)) {
    errs.push({
      code: 'strokeColor.invalid',
      message: `strokeColor "${input.strokeColor}" must be a valid CSS color`,
      path: ['strokeColor'],
    });
  }
  if (!isColorLike(input.fillColor)) {
    errs.push({
      code: 'fillColor.invalid',
      message: `fillColor "${input.fillColor}" must be a valid CSS color`,
      path: ['fillColor'],
    });
  }

  // Points length per tool
  const pointsLen = input.points.length;
  const minPoints = requiredPointsForTool(input.type);
  if (pointsLen < minPoints) {
    errs.push({
      code: 'points.too_few',
      message: `tool ${input.type} requires at least ${minPoints} points (got ${pointsLen})`,
      path: ['points'],
    });
  }

  // NaN checks for points
  for (let i = 0; i < input.points.length; i++) {
    const p = input.points[i];
    if (!Number.isFinite(p.x) || !Number.isFinite(p.y)) {
      errs.push({
        code: 'points.invalid',
        message: `point[${i}] has non-finite coordinates`,
        path: ['points', String(i)],
      });
    }
  }

  if (errs.length > 0) {
    return { ok: false, error: errs };
  }
  return { ok: true, value: input };
}

function requiredPointsForTool(tool: ElementType): number {
  if (tool === 'text') return 1;
  // All drawable stroke-based tools require at least 2 points
  if (tool === 'pencil' || tool === 'line' || tool === 'arrow' || tool === 'rectangle' || tool === 'circle') {
    return 2;
  }
  // Fallback
  return 1;
}

function isValidType(type: unknown): type is ElementType {
  return (
    type === 'pencil' ||
    type === 'line' ||
    type === 'arrow' ||
    type === 'rectangle' ||
    type === 'circle' ||
    type === 'text' ||
    type === 'eraser'
  );
}

export function isColorLike(value: unknown): boolean {
  if (typeof value !== 'string') return false;
  const v = value.trim();
  if (v.length === 0) return false;
  // Hex: #RGB, #RGBA, #RRGGBB, #RRGGBBAA
  if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(v)) return true;
  // rgb()/rgba(): integers 0..255, optional alpha 0..1
  if (/^rgb\(\s*(\d{1,3}\s*,){2}\s*\d{1,3}\s*\)$/.test(v)) return true;
  if (/^rgba\(\s*(\d{1,3}\s*,){3}\s*(0|1|0?\.\d+)\s*\)$/.test(v)) return true;
  // hsl()/hsla(): h 0..360, s/l in %, optional alpha 0..1 (loose check)
  if (/^hsl\(\s*\d{1,3}\s*,\s*\d{1,3}%\s*,\s*\d{1,3}%\s*\)$/.test(v)) return true;
  if (/^hsla\(\s*\d{1,3}\s*,\s*\d{1,3}%\s*,\s*\d{1,3}%\s*,\s*(0|1|0?\.\d+)\s*\)$/.test(v)) return true;
  // Named colors (subset) + transparent
  const named = new Set([
    'transparent','black','white','red','green','blue','gray','grey','yellow','cyan','magenta',
    'orange','purple','pink','brown','lime','teal','navy','maroon','olive','silver','aqua','fuchsia'
  ]);
  if (named.has(v.toLowerCase())) return true;
  return false;
}

